import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  courseSectionsService,
  CreateSectionDto,
  UpdateSectionDto,
  AddProductDto,
  ReorderSectionsDto,
  ReorderProductsDto,
} from '@/services/course-sections.service';
export type { PublicSectionsResponse } from '@/services/course-sections.service';

export const courseSectionKeys = {
  all: ['course-sections'] as const,
  public: () => [...courseSectionKeys.all, 'public'] as const,
  lists: () => [...courseSectionKeys.all, 'list'] as const,
  list: () => [...courseSectionKeys.lists()] as const,
  details: () => [...courseSectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...courseSectionKeys.details(), id] as const,
};

// ========== Public Queries ==========

/**
 * Hook para listar seções públicas (visíveis com produtos publicados)
 * Usado na página de cursos para alunos
 */
export function useCourseSections() {
  return useQuery({
    queryKey: courseSectionKeys.public(),
    queryFn: () => courseSectionsService.findAllPublic(),
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
}

// ========== Admin Queries ==========

/**
 * Hook para listar todas as seções (admin)
 */
export function useAdminCourseSections() {
  return useQuery({
    queryKey: courseSectionKeys.list(),
    queryFn: () => courseSectionsService.findAll(),
  });
}

/**
 * Hook para buscar seção por ID (admin)
 */
export function useCourseSection(id: string, enabled = true) {
  return useQuery({
    queryKey: courseSectionKeys.detail(id),
    queryFn: () => courseSectionsService.findOne(id),
    enabled: enabled && !!id,
  });
}

// ========== Mutations ==========

/**
 * Hook para criar nova seção
 */
export function useCreateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSectionDto) => courseSectionsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
    },
  });
}

/**
 * Hook para atualizar seção
 */
export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSectionDto }) =>
      courseSectionsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
      queryClient.invalidateQueries({
        queryKey: courseSectionKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Hook para deletar seção
 */
export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => courseSectionsService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
    },
  });
}

/**
 * Hook para reordenar seções
 */
export function useReorderSections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderSectionsDto) =>
      courseSectionsService.reorder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
    },
  });
}

/**
 * Hook para adicionar produto a uma seção
 */
export function useAddProductToSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: AddProductDto;
    }) => courseSectionsService.addProduct(sectionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
      queryClient.invalidateQueries({
        queryKey: courseSectionKeys.detail(variables.sectionId),
      });
    },
  });
}

/**
 * Hook para remover produto de uma seção
 */
export function useRemoveProductFromSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sectionId,
      productId,
    }: {
      sectionId: string;
      productId: string;
    }) => courseSectionsService.removeProduct(sectionId, productId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
      queryClient.invalidateQueries({
        queryKey: courseSectionKeys.detail(variables.sectionId),
      });
    },
  });
}

/**
 * Hook para reordenar produtos dentro de uma seção
 */
export function useReorderProductsInSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: ReorderProductsDto;
    }) => courseSectionsService.reorderProducts(sectionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: courseSectionKeys.all });
      queryClient.invalidateQueries({
        queryKey: courseSectionKeys.detail(variables.sectionId),
      });
    },
  });
}
